// Module Imports
import { useEffect, useMemo, useState } from "react"
import {
  MRT_ColumnDef,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table"
import { useDispatch, useSelector } from "react-redux"

// Redux Actions
import {
  setExpandedD,
  setMetricOpportunityTableData,
  setMetricOpportunityTableDataFiltered,
} from "../../../redux/slices/metricSlice"

// Styled Components
import { NoBucketWrapper, TableWrapper } from "../Atom"

// Components
import { MetricOpportunityColumnFilter } from "../../Filters"
import MetricOpportunitySearchFilter from "../../Filters/MetricOpportunitySearchFilter"
import OpportunityMetricNameCell from "../Cell/OpportunityMetricNameCell"
import OpportunityCell from "../Cell/OpportunityCell"
import { metricNameCellStyles } from "./Atom"
import OpportunityHeader from "../Header/OpportunityHeader"
import { colors } from "../../../../styles/commonStyles"
import Loader from "../../Loader"
import { Box, CircularProgress } from "@mui/material"
import { metricHeaderPaddingByColumnId } from "../../../../config/constants"
import { metricStoreDataDummy } from "../../../../config/constants/dummyData"
import { parse } from "date-fns"
import {
  processPracticesData,
  processRegionsData,
} from "../../../../helper/utils/dataMapping"
import { setLoading } from "../../../redux/slices/appSlice"

type MetricOpportunityTableProps = {
  opportunityData: any
  fetchNestedData: any
}

const MetricOpportunityTable = (props: MetricOpportunityTableProps) => {
  const { opportunityData, fetchNestedData } = props

  const opportunityTableDataFiltered = useSelector((state: any) => {
    return state.metric.metricOpportunityTableDataFiltered
  })

  useEffect(() => {
    console.log("opportunityTableDataFiltered", opportunityTableDataFiltered)
  }, [opportunityTableDataFiltered])

  const maevaSelectedGroup = useSelector((state: any) => {
    return state.metric.maevaSelectedGroupsNoAll?.[0]
  })

  const maevaSelectedRegion = useSelector((state: any) => {
    return state.metric.maevaSelectedRegions?.[0]
  })

  const maevaSelectedPractice = useSelector((state: any) => {
    return state.metric.maevaSelectedPractices?.[0]
  })

  const loading = useSelector((state: any) => state.app.loading)

  const selectedDateFilter = useSelector((state: any) => {
    return state.metric.metricOpportunityHeaderFilterSelectedYear
  })

  const selectedMetricOpportunityTab = useSelector((state: any) => {
    return state.metric.selectedMetricOpportunityTab
  })
  const expandedD = useSelector((state: any) => {
    return state.metric.expandedD
  })

  // const [expandedD, setExpandedD] = useState<{ [key: string]: boolean }>({})

  const dispatch = useDispatch()

  /**
   * Processes and structures metric data into a hierarchical format, then dispatches this structured data to update the application's state.
   * @param selectedMetricOpportunityTabTemp Optional parameter representing the selected metric opportunity tab data.
   */
  const populateOpportunityTable = (selectedMetricOpportunityTabTemp?: any) => {
    let temp: any = []
    let tempObjG: any = {}

    if (!selectedMetricOpportunityTabTemp) {
      selectedMetricOpportunityTabTemp = selectedMetricOpportunityTab
    }

    selectedMetricOpportunityTabTemp?.metrics?.forEach((metric: any) => {
      let tempRegionsArr: any = metric?.subRows?.map((region: any) => {
        let tempPracticesArray: any = region?.subRows?.map((practice: any) => {
          let tempObjP: any = {
            ...practice,
            metric_name: practice.name,
            metric_original_name: metric?.metric_name,
            group_sk: metric?.group?.group_sk,
            group_name: metric?.group?.group_name,
            metric_id: metric?.metric_id,
            metric_precision: metric?.metric_precision,
          }

          practice?.storeData?.forEach((column: any) => {
            tempObjP[`${column?.column_id}`] = column?.column_value
            tempObjP[`${column?.column_id}_color`] = column?.column_color
            tempObjP[`${column?.column_id}_datatype`] = column?.column_datatype
          })

          return tempObjP
        })

        if (maevaSelectedPractice && maevaSelectedPractice?.name !== "all") {
          tempPracticesArray = tempPracticesArray.filter(
            (tempPractice: any) =>
              tempPractice?.practice_sk === maevaSelectedPractice?.practice_sk,
          )
        }

        let tempObjR: any = {
          ...region,
          subRows: [...tempPracticesArray],
          metric_name: region?.region,
          group_sk: metric?.group?.group_sk,
          group_name: metric?.group?.group_name,
          metric_id: metric?.metric_id,
          goal_type: metric?.goal_type,
          metric_precision: metric?.metric_precision,
        }

        region?.storeData?.forEach((column: any) => {
          tempObjR[`${column?.column_id}`] = column?.column_value
          tempObjR[`${column?.column_id}_color`] = column?.column_color
          tempObjR[`${column?.column_id}_datatype`] = column?.column_datatype
        })

        return tempObjR
      })

      if (maevaSelectedRegion && maevaSelectedRegion?.name !== "all") {
        tempRegionsArr = tempRegionsArr?.filter(
          (tempRegion: any) => tempRegion?.region === maevaSelectedRegion?.name,
        )
      }

      tempObjG = {
        ...metric,
        subRows: tempRegionsArr?.length ? [...tempRegionsArr] : [],
        group_sk: metric?.group?.group_sk,
        group_name: metric?.group?.group_name,
        baseline_value: metric?.group?.baseline_value || "Not Available",
        metric_benchmark: metric?.metric_benchmark || "Not Available",
        childrenHasOps: metric?.group?.childrenHasOps,
        level: 0,
      }

      metric?.group?.storeData?.forEach((column: any) => {
        tempObjG[`${column?.column_id}`] = column?.column_value
        tempObjG[`${column?.column_id}_color`] = column?.column_color
        tempObjG[`${column?.column_id}_datatype`] = column?.column_datatype
      })

      temp.push({ ...tempObjG })
    })

    dispatch(setMetricOpportunityTableData([...temp]))
    dispatch(setMetricOpportunityTableDataFiltered([...temp]))
  }

  function filterColors(
    selectedMetricOpportunityTabTemp: any,
    opportunityTableData: any,
    opportunityTableDataFiltered: any,
    filterColor: any,
    filterIndex: any,
    selectValues: any,
    headerIndex: any,
  ) {
    if (filterColor == "all") {
      populateOpportunityTable(selectedMetricOpportunityTabTemp)
      return
    }

    let existingData: any

    if (selectValues[headerIndex] != "") {
      existingData = opportunityTableData
    } else {
      existingData = opportunityTableDataFiltered
    }

    const filterPracticesByColor = (
      practices: any,
      filterColor: any,
      filterIndex: any,
    ) => {
      return practices.filter((practice: any) => {
        const columns = practice?.storeData?.filter(
          (column: any) =>
            column?.column_color == filterColor &&
            column?.column_id == filterIndex,
        )

        return columns?.length > 0
      })
    }

    const filteredData = existingData.map((metric: any) => {
      const filteredRegions = metric.subRows
        .map((region: any) => {
          const filteredPractices = filterPracticesByColor(
            region.subRows,
            filterColor,
            filterIndex,
          )

          return {
            ...region,
            subRows: filteredPractices,
          }
        })
        .filter((filteredRegion: any) => filteredRegion.subRows.length > 0)

      return {
        ...metric,
        group: {
          ...metric.group,
          subRows: filteredRegions,
        },
        subRows: filteredRegions,
      }
    })

    dispatch(setMetricOpportunityTableDataFiltered(filteredData))
  }

  const handleSearchFn = (
    opportunityTableData: any,
    str: string,
    selectedMetricOpportunityTabTemp: any,
  ) => {
    if (str?.trim() == "") {
      populateOpportunityTable(selectedMetricOpportunityTabTemp)
      return
    }

    let temp: any = []

    temp = opportunityTableData?.filter((metric: any) =>
      metric?.metric_name.toLowerCase().includes(str.toLowerCase()),
    )

    dispatch(setMetricOpportunityTableDataFiltered([...temp]))
  }

  useEffect(() => {
    populateOpportunityTable()
  }, [
    opportunityData,
    maevaSelectedGroup,
    maevaSelectedRegion,
    maevaSelectedPractice,
    selectedMetricOpportunityTab,
  ])

  const columns = useMemo<any[]>(() => {
    let localArr = [
      {
        accessorKey: "metric_name",
        Header: ({ row }: any) => (
          <Box sx={{ paddingBottom: "17px", paddingTop: "17px" }}>
            <OpportunityHeader headerName={`Metric Name`} width={`300px`} />
          </Box>
        ),
        Cell: ({ row, renderedCellValue }: any) => (
          <OpportunityMetricNameCell
            row={row}
            renderedCellValue={renderedCellValue}
          />
        ),
        size: 100,
        Filter: ({ header, column }: any) => (
          <MetricOpportunitySearchFilter
            header={header}
            column={column}
            handleSearchFn={handleSearchFn}
          />
        ),
        filterFn: (row: any, _columnIds: any, filterValue: any) =>
          row?.original?.metric_name
            .toLowerCase()
            .includes(filterValue.toLowerCase()),
        enableEditing: false,
        enablePinning: true,
        enableColumnFilter: true,
        muiTableBodyCellProps: () => ({
          sx: metricNameCellStyles,
        }),
      },
      ...(selectedMetricOpportunityTab?.columnHeaders?.map(
        (column: any, index: number) => {
          const columnId = column?.column_id
          const columnHeader = column?.column_name
          const columnHeaderPaddingTop =
            column?.headerPaddingTop ??
            metricHeaderPaddingByColumnId[columnId] ??
            "5px"

          return {
            header: columnHeader,
            id: columnId,
            accessorKey: `${column?.column_id}`,
            size: 150,
            Header: ({ column }: any) => (
              <Box
                title={columnHeader}
                sx={{
                  maxWidth: column.getSize(),
                  overflow: "hidden",
                  "& .MuiTypography-root": {
                    textWrap: "wrap",
                    padding: "5px 6px",
                    paddingTop: columnHeaderPaddingTop,
                  },
                }}
              >
                <OpportunityHeader headerName={columnHeader} />
              </Box>
            ),
            Cell: ({ column, row, renderedCellValue }: any) => (
              <OpportunityCell
                row={row}
                renderedCellValue={renderedCellValue}
                column={column}
              />
            ),
            Filter: ({ header, column }: any) => (
              <MetricOpportunityColumnFilter
                header={header}
                column={column}
                filterColors={filterColors}
              />
            ),
          }
        },
      ) ?? []),
    ]
    return loading ? [] : localArr
  }, [
    selectedDateFilter,
    selectedMetricOpportunityTab?.metric_bucket,
    loading,
    opportunityTableDataFiltered,
  ])

  const handleExpandedChange = async (expanded: any) => {
    const expandedS = typeof expanded === "function" ? expanded() : expanded

    const expandedArray = Object.keys(expandedS).filter((key) => expandedS[key])

    console.log("expandedArray", expandedArray)

    // If no expanded rows, just return
    if (expandedArray.length === 0) {
      dispatch(setExpandedD({}))
      return
    }

    let level0 = parseInt(expandedArray[0].split(".")[0])
    let level1 = parseInt(expandedArray[0]?.split(".")[1])

    const clickedRow = expandedArray[0]
    const newExpanded = { ...expandedD }

    if (clickedRow in newExpanded) {
      delete newExpanded[clickedRow]
    } else {
      newExpanded[clickedRow] = true
    }

    dispatch(setExpandedD(newExpanded))

    const row = !Number.isNaN(level1)
      ? opportunityTableDataFiltered[level0]?.subRows[level1]
      : opportunityTableDataFiltered[level0]

    if (row && (!row.subRows || row.subRows.length === 0)) {
      const data = await fetchNestedData(
        selectedMetricOpportunityTab?.metric_bucket_id,
        row?.metric_id,
        row?.level,
        row?.region,
      )

      let processedData
      if (row?.level == 0) {
        // For regions
        processedData = processRegionsData(data, row)

        // Apply region filter if selected
        if (maevaSelectedRegion && maevaSelectedRegion?.name !== "all") {
          processedData = processedData.filter(
            (region: any) => region?.region === maevaSelectedRegion?.name,
          )
        }

        // Additional practice filter for regions
        if (maevaSelectedPractice && maevaSelectedPractice?.name !== "all") {
          processedData = processedData.filter(
            (region: any) =>
              region.subRows &&
              region.subRows.some(
                (practice: any) =>
                  practice?.practice_sk === maevaSelectedPractice?.practice_sk,
              ),
          )
        }
      } else {
        // For practices
        processedData = processPracticesData(data, row)

        // Apply practice (office) filter if selected
        if (maevaSelectedPractice && maevaSelectedPractice?.name !== "all") {
          processedData = processedData.filter(
            (practice: any) =>
              practice?.practice_sk === maevaSelectedPractice?.practice_sk,
          )
        }
      }

      // If no data after filtering, use original data
      if (processedData.length === 0) {
        processedData =
          row?.level == 0
            ? processRegionsData(data, row)
            : processPracticesData(data, row)
      }

      const updatedRow = {
        ...row,
        subRows: processedData,
      }

      let updatedData = opportunityTableDataFiltered.map(
        (item: any, index: any) => {
          if (index === level0) {
            if (!Number.isNaN(level1)) {
              // Update specific subrow
              return {
                ...item,
                subRows: item.subRows.map((subItem: any, subIndex: any) =>
                  subIndex === level1 ? updatedRow : subItem,
                ),
              }
            }
            // Update entire row
            return updatedRow
          }
          return item
        },
      )

      dispatch(setMetricOpportunityTableData(updatedData))
      dispatch(setMetricOpportunityTableDataFiltered(updatedData))
    }
  }

  // Add a useEffect to handle filter changes and expanded state
  useEffect(() => {
    // When filters change, adjust expanded state

    const newExpanded: { [key: string]: boolean } = {}

    opportunityTableDataFiltered.forEach((metric: any, metricIndex: any) => {
      if (metric.subRows && metric.subRows.length > 0) {
        // Keep metric row expanded
        console.log("metric", metric)
        console.log("metric.subRows", metric.subRows)

        if (expandedD[`${metricIndex}`]) {
          newExpanded[`${metricIndex}`] = true
        }
        metric.subRows.forEach((region: any, regionIndex: any) => {
          if (region.subRows && region.subRows.length > 0) {
            // Keep region row expanded
            if (expandedD[`${metricIndex}.${regionIndex}`]) {
              newExpanded[`${metricIndex}.${regionIndex}`] = true
            }
          }
        })
      }
    })

    dispatch(setExpandedD(newExpanded))
  }, [maevaSelectedPractice, maevaSelectedRegion, opportunityTableDataFiltered])

  const tableConfig: MRT_TableOptions<any> = useMemo(
    () => ({
      columns,
      data: opportunityTableDataFiltered,
      state: {
        isLoading: loading,
        expanded: expandedD,
      },
      enableEditing: false,
      enableColumnActions: false,
      enableColumnFilters: true,
      enablePagination: false,
      enableSorting: false,
      enableBottomToolbar: false,
      enableStickyHeader: true,
      enableTopToolbar: false,
      enablePinning: true,
      enableExpanding: true,
      initialState: {
        showColumnFilters: true,
        columnVisibility: { "mrt-row-expand": false },
        columnPinning: {
          left: ["mrt-row-expand", "metric_name"],
        },
      },
      autoResetExpanded: true,
      getSubRows: (originalRow: any) => originalRow?.subRows,
      getRowCanExpand: (row: any) => row.original.level !== 2,
      onExpandedChange: handleExpandedChange,
      displayColumnDefOptions: {
        "mrt-row-expand": {
          enablePinning: true,
        },
      },
      muiTableContainerProps: {
        sx: {
          flex: 1,
          height: "72vh",
        },
      },
      muiTableHeadRowProps: {
        sx: {
          padding: "0",
        },
      },
      muiTableHeadCellProps: {
        align: "center",
        sx: {
          border: "0.5px solid #E4E4E4",
          backgroundColor: colors.primaryOrange,
          color: colors.primaryWhite,
          padding: "0 !important",
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
        },
      },
      muiTableBodyRowProps: {
        hover: false,
        sx: {
          padding: "0 !important",
          height: "64px",
          textAlign: "center",
        },
      },
      muiTableBodyCellProps: {
        sx: {
          padding: "0 !important",
          border: "0.5px solid #E4E4E4",
          textAlign: "center",
        },
      },
    }),
    [
      columns,
      opportunityTableDataFiltered,
      loading,
      selectedMetricOpportunityTab?.metric_bucket,
      expandedD,
    ],
  )

  return (
    <TableWrapper>
      {Object.keys(selectedMetricOpportunityTab || {}).length !== 0 &&
      !loading ? (
        <MaterialReactTable {...tableConfig} />
      ) : (
        <NoBucketWrapper>
          {loading ? <CircularProgress /> : "Select A Bucket"}
        </NoBucketWrapper>
      )}
    </TableWrapper>
  )
}

export default MetricOpportunityTable
